import { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Tabs, TabsList, TabsTrigger, TabsContent,
} from "@/components/ui/tabs";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { toast } from "sonner";
import { Loader2, Clock, CalendarDays, Plus } from "lucide-react";

/**
 * Phase 33 — Admin / worker manual time-entry dialog with punch | duration toggle.
 *
 * - Admin surface (`endpoint="/admin/time-entries"`): can pick any worker in-tenant.
 * - Worker surface (`endpoint="/me/time-entries"`): no worker picker; duration-only;
 *   caller already gated by `allow_worker_duration_entry` on the tenant.
 *
 * Field-level 422 bodies from the backend are rendered inline on the offending
 * field AND surfaced as a sonner toast so blind-filling the form never silently
 * fails.
 */
export function AdminTimeEntryDialog({
  open,
  onOpenChange,
  endpoint = "/admin/time-entries",
  workers = [],
  worksites = [],
  defaultEntryMode = "punch",
  defaultWorkerId = "",
  lockToWorkerId = null,          // worker-self: force this user_id (hidden picker)
  allowPunchMode = true,          // worker-self: duration only
  onCreated,
}) {
  const [mode, setMode] = useState(defaultEntryMode);
  const [workerId, setWorkerId] = useState(defaultWorkerId);
  const [worksiteId, setWorksiteId] = useState("");

  // Duration mode state.
  const [workDate, setWorkDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [hours, setHours] = useState("8");
  const [minutes, setMinutes] = useState("0");

  // Punch mode state.
  const [clockInLocal, setClockInLocal] = useState("");
  const [clockOutLocal, setClockOutLocal] = useState("");

  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const [fieldError, setFieldError] = useState({ field: "", message: "" });

  useEffect(() => {
    if (!open) return;
    setMode(allowPunchMode ? defaultEntryMode : "duration");
    setWorkerId(lockToWorkerId || defaultWorkerId || "");
    setWorksiteId("");
    setWorkDate(new Date().toISOString().slice(0, 10));
    setHours("8");
    setMinutes("0");
    setClockInLocal("");
    setClockOutLocal("");
    setNotes("");
    setFieldError({ field: "", message: "" });
  }, [open, allowPunchMode, defaultEntryMode, defaultWorkerId, lockToWorkerId]);

  const durationMinutes = useMemo(() => {
    const h = parseInt(hours, 10);
    const m = parseInt(minutes, 10);
    if (Number.isNaN(h) || Number.isNaN(m)) return null;
    return h * 60 + m;
  }, [hours, minutes]);

  const isoFromLocal = (s) => {
    // Interpret as local time; convert to ISO-8601 with the browser's offset.
    if (!s) return null;
    const d = new Date(s);
    if (Number.isNaN(d.getTime())) return null;
    return d.toISOString();
  };

  const submit = async () => {
    setBusy(true);
    setFieldError({ field: "", message: "" });
    try {
      const body = {
        entry_mode: mode,
        notes: notes || undefined,
      };
      if (!lockToWorkerId) {
        if (!workerId) {
          setFieldError({ field: "user_id", message: "Worker is required" });
          setBusy(false);
          return;
        }
        body.user_id = workerId;
      }
      if (worksiteId) body.worksite_id = worksiteId;
      if (mode === "duration") {
        body.work_date = workDate;
        body.duration_minutes = durationMinutes;
      } else {
        body.clock_in_at = isoFromLocal(clockInLocal);
        if (clockOutLocal) body.clock_out_at = isoFromLocal(clockOutLocal);
      }
      const res = await api.post(endpoint, body);
      toast.success(
        mode === "duration"
          ? `Logged ${hours}h ${minutes}m for ${workDate}`
          : "Time entry created"
      );
      onCreated?.(res.data);
      onOpenChange(false);
    } catch (e) {
      // Flat body shapes:
      //   422 field validation → {error, field, message}
      //   409 period_locked     → {error:"period_locked", period, entry_mode,
      //                            timesheet_id, use_correction_flow}
      //   409 overlap / clocked → {error:"punch_overlap"|"already_clocked_in", ...}
      const detail = e?.response?.data;
      if (detail?.error === "period_locked") {
        // F10 — human-readable toast instead of the generic
        // "Request failed with status code 409" that the previous
        // tester saw.
        const period = detail.period || "that week";
        toast.error(
          `This week is locked and can't be edited (${period}). ` +
          `Unlock the timesheet first, then try again.`,
          { duration: 7000 }
        );
      } else if (detail?.error === "already_clocked_in") {
        toast.error(
          "This worker already has an open punch. Close it before " +
          "creating another."
        );
      } else if (detail?.error === "punch_overlap") {
        toast.error(
          "The new punch would overlap an existing one. Adjust the " +
          "time range."
        );
      } else if (detail?.field && detail?.message) {
        setFieldError({ field: detail.field, message: detail.message });
        toast.error(`${detail.field}: ${detail.message}`);
      } else {
        toast.error(formatApiError(e));
      }
    } finally {
      setBusy(false);
    }
  };

  const fieldErr = (name) =>
    fieldError.field === name ? (
      <p className="mt-1 text-xs text-destructive" data-testid={`field-error-${name}`}>
        {fieldError.message}
      </p>
    ) : null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="admin-time-entry-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Plus className="h-5 w-5" /> New time entry
          </DialogTitle>
          <DialogDescription>
            {allowPunchMode
              ? "Choose punch mode for live clock-in/out, or duration mode to log past work by date + hours."
              : "Log past work by date + hours worked."}
          </DialogDescription>
        </DialogHeader>

        <Tabs
          value={mode}
          onValueChange={(v) => {
            setMode(v);
            setFieldError({ field: "", message: "" });
          }}
          className="space-y-4"
        >
          {allowPunchMode && (
            <TabsList className="grid w-full grid-cols-2" data-testid="entry-mode-tabs">
              <TabsTrigger value="punch" data-testid="entry-mode-punch">
                <Clock className="mr-1.5 h-3.5 w-3.5" /> Punch
              </TabsTrigger>
              <TabsTrigger value="duration" data-testid="entry-mode-duration">
                <CalendarDays className="mr-1.5 h-3.5 w-3.5" /> Date + duration
              </TabsTrigger>
            </TabsList>
          )}

          {!lockToWorkerId && (
            <div className="space-y-1.5">
              <Label>Worker</Label>
              <Select value={workerId} onValueChange={setWorkerId}>
                <SelectTrigger data-testid="entry-worker-select">
                  <SelectValue placeholder="Choose worker" />
                </SelectTrigger>
                <SelectContent>
                  {workers.map((u) => (
                    <SelectItem key={u.id} value={u.id}>
                      {u.full_name || u.email}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {fieldErr("user_id")}
            </div>
          )}

          <TabsContent value="punch" className="space-y-4">
            <div className="space-y-1.5">
              <Label>Worksite</Label>
              <Select value={worksiteId} onValueChange={setWorksiteId}>
                <SelectTrigger data-testid="entry-worksite-select">
                  <SelectValue placeholder="Choose worksite" />
                </SelectTrigger>
                <SelectContent>
                  {worksites.map((w) => (
                    <SelectItem key={w.id} value={w.id}>
                      {w.name} ({w.code})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {fieldErr("worksite_id")}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Clock in (local)</Label>
                <Input
                  type="datetime-local"
                  value={clockInLocal}
                  onChange={(e) => setClockInLocal(e.target.value)}
                  data-testid="entry-clock-in"
                />
                {fieldErr("clock_in_at")}
              </div>
              <div className="space-y-1.5">
                <Label>Clock out (optional)</Label>
                <Input
                  type="datetime-local"
                  value={clockOutLocal}
                  onChange={(e) => setClockOutLocal(e.target.value)}
                  data-testid="entry-clock-out"
                />
                {fieldErr("clock_out_at")}
              </div>
            </div>
          </TabsContent>

          <TabsContent value="duration" className="space-y-4">
            <div className="space-y-1.5">
              <Label>Worksite (optional)</Label>
              <Select
                value={worksiteId || "none"}
                onValueChange={(v) => setWorksiteId(v === "none" ? "" : v)}
              >
                <SelectTrigger data-testid="entry-worksite-select-dur">
                  <SelectValue placeholder="Not tied to a worksite" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">— none —</SelectItem>
                  {worksites.map((w) => (
                    <SelectItem key={w.id} value={w.id}>
                      {w.name} ({w.code})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {fieldErr("worksite_id")}
            </div>
            <div className="space-y-1.5">
              <Label>Work date</Label>
              <Input
                type="date"
                value={workDate}
                onChange={(e) => setWorkDate(e.target.value)}
                data-testid="entry-work-date"
              />
              {fieldErr("work_date")}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Hours</Label>
                <Input
                  type="number"
                  min="0"
                  max="16"
                  step="1"
                  value={hours}
                  onChange={(e) => setHours(e.target.value)}
                  data-testid="entry-hours"
                />
                {fieldErr("duration_minutes")}
              </div>
              <div className="space-y-1.5">
                <Label>Minutes</Label>
                <Input
                  type="number"
                  min="0"
                  max="59"
                  step="1"
                  value={minutes}
                  onChange={(e) => setMinutes(e.target.value)}
                  data-testid="entry-minutes"
                />
              </div>
            </div>
            {durationMinutes != null && durationMinutes > 0 && (
              <p className="text-xs text-muted-foreground">
                Total: {durationMinutes} minutes
              </p>
            )}
          </TabsContent>
        </Tabs>

        <div className="space-y-1.5">
          <Label>Notes (optional)</Label>
          <Input
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            data-testid="entry-notes"
            placeholder="e.g. offsite training"
          />
        </div>

        {fieldError.field && !["user_id", "worksite_id", "clock_in_at", "clock_out_at", "work_date", "duration_minutes"].includes(fieldError.field) && (
          <Alert variant="destructive">
            <AlertDescription data-testid="entry-general-error">
              {fieldError.field}: {fieldError.message}
            </AlertDescription>
          </Alert>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            onClick={submit}
            disabled={busy}
            data-testid="entry-submit-btn"
          >
            {busy && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />}
            Create entry
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
